import React, { useEffect, useRef, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import useDebouncedChange from '../useDebouncedChange';
import { useCloseOnEscape } from './closeOnEscape';
import { useMenuHeight } from './menuPlacement';
import './FilterPill.css';

const optionCountBeforeSearchBox = 8;

export const pillShape = { padding: '0.45rem 0.9rem', fontSize: '0.875rem', lineHeight: 1.5, borderRadius: '2rem' };

const valueWidth = { maxWidth: '14rem' };
const menuWidth = { minWidth: '15rem', maxWidth: 'min(24rem, calc(100vw - 2rem))' };

const footerButtonClass = (disabled: boolean) =>
  `btn btn-sm text-nowrap border ${disabled ? 'text-muted' : 'btn-outline-primary border-primary'}`;

export interface MultiSelectPillOption {
  value: string;
  label: string;
  pillLabel?: string;
}

export interface MultiSelectPillSection {
  label: string;
  note?: string;
  options: MultiSelectPillOption[];
}

export interface MultiSelectPillProps {
  id: string;
  label: string;
  icon?: string;
  allLabel?: string;
  emptyLabel?: string;
  extraOption?: MultiSelectPillOption;
  values: string[];
  options: MultiSelectPillOption[];
  sections?: MultiSelectPillSection[];
  debounceMs?: number;
  onChange: (values: string[]) => void;
}

const alphabetically = (first: MultiSelectPillOption, second: MultiSelectPillOption) =>
  first.label.localeCompare(second.label, undefined, { numeric: true });

const matching = (options: MultiSelectPillOption[], query: string) =>
  options.filter(option => option.label.toLowerCase().includes(query.toLowerCase()));

const FilterOption = ({ option, checked, onToggle }: {
  option: MultiSelectPillOption;
  checked: boolean;
  onToggle: () => void;
}) =>
  <label className="dropdown-item d-flex align-items-center gap-2 mb-0" style={{ cursor: 'pointer' }}>
    <input
      type="checkbox"
      className="form-check-input mt-0 flex-shrink-0"
      checked={checked}
      onChange={onToggle} />
    <span className="text-truncate" title={option.label}>{option.label}</span>
  </label>;

export default function MultiSelectPill(props: MultiSelectPillProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const pillRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const { maxHeight, measure } = useMenuHeight(pillRef, open);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  const { value: values, change, flush } = useDebouncedChange(props.values, props.onChange, props.debounceMs);

  const close = () => {
    flush();
    setOpen(false);
    setQuery('');
  };

  useCloseOnEscape(pillRef, open, close);

  const options = [...props.options].sort(alphabetically);
  const sections = (props.sections ?? []).map(section => ({
    ...section,
    options: [...section.options].sort(alphabetically),
  }));
  const listed = [...options, ...sections.flatMap(section => section.options)];
  const selectable = props.extraOption ? [...listed, props.extraOption] : listed;
  const isSelected = (option: MultiSelectPillOption) => values.includes(option.value);
  const selected = selectable.filter(isSelected);
  const allSelected = selected.length === selectable.length && selectable.length > 0;
  const searchable = listed.length > optionCountBeforeSearchBox;
  const visible = matching(options, query);
  const visibleSections = sections
    .map(section => ({ ...section, options: matching(section.options, query) }))
    .filter(section => section.options.length > 0);
  const nothingVisible = visible.length === 0 && visibleSections.length === 0;

  const toggle = (value: string) => change(
    values.includes(value)
      ? values.filter(current => current !== value)
      : [...values, value]);

  const active = values.length > 0;
  const nothingSelected = selected.length === 0;
  const noun = props.label.toLowerCase();
  const pillLabelOf = (option: MultiSelectPillOption) => option.pillLabel ?? option.label;
  const selectedOptions = options.filter(isSelected);
  const extraSelected = props.extraOption !== undefined && isSelected(props.extraOption);
  const ownSelected = selectedOptions.length === options.length && options.length > 0
    && (props.extraOption === undefined || extraSelected);

  const allText = ownSelected ? props.allLabel : undefined;
  const ownText = allText ?? (
    selectedOptions.length === 1
      ? pillLabelOf(selectedOptions[0])
      : selectedOptions.length > 1
        ? `${selectedOptions.length} ${noun}`
        : undefined);
  const extraText = extraSelected && allText === undefined ? pillLabelOf(props.extraOption!) : undefined;
  const sectionSelected = sections.flatMap(section => section.options).filter(isSelected);
  const sectionNoun = sections[0]?.note ?? props.label.toLowerCase();
  const sectionTexts = sectionSelected.length === 0
    ? []
    : ownText === undefined && sectionSelected.length === 1
      ? [pillLabelOf(sectionSelected[0])]
      : [`${sectionSelected.length} ${sectionNoun}`];

  const valueText = nothingSelected
    ? props.emptyLabel ?? props.label
    : [ownText, extraText, ...sectionTexts].filter(text => text !== undefined).join(' + ');

  return (
    <div className="d-inline-flex align-items-stretch mw-100" ref={pillRef}>
      <Dropdown
        show={open}
        onToggle={next => {
          if (!next) return close();
          measure();
          setOpen(true);
        }}
        autoClose="outside">
        <Dropdown.Toggle
          as="button"
          type="button"
          id={`${props.id}-toggle`}
          className={`filter-pill d-inline-flex align-items-center text-nowrap border bg-white${active ? ' active' : ''}`}
          style={pillShape}>
          {props.icon && <i className={`bi-${props.icon} me-2`} aria-hidden="true" />}
          <span className="fw-semibold text-truncate" style={valueWidth}>{valueText}</span>
        </Dropdown.Toggle>
        <Dropdown.Menu
          role="group"
          className="filter-pill-menu pt-1 shadow-lg overflow-hidden"
          style={{ ...menuWidth, maxHeight }}
          popperConfig={{ modifiers: [{ name: 'preventOverflow', options: { padding: 8 } }] }}>
          {searchable &&
            <input
              type="text"
              className="form-control form-control-sm mx-2 mt-1 mb-2 flex-shrink-0"
              style={{ width: 'calc(100% - 1rem)' }}
              placeholder={`Search ${noun}…`}
              ref={searchRef}
              value={query}
              onChange={e => setQuery(e.target.value)} />}
          <div className="overflow-auto flex-grow-1" style={{ minHeight: 0 }}>
            {visible.map(option =>
              <FilterOption
                key={option.value}
                option={option}
                checked={values.includes(option.value)}
                onToggle={() => toggle(option.value)} />)}
            {visibleSections.map(section =>
              <React.Fragment key={section.label}>
                <div className="dropdown-divider"></div>
                <div className="dropdown-header">
                  <span className="text-dark fw-semibold">{section.label}</span>
                  {section.note && <> ({section.note})</>}
                </div>
                {section.options.map(option =>
                  <FilterOption
                    key={option.value}
                    option={option}
                    checked={values.includes(option.value)}
                    onToggle={() => toggle(option.value)} />)}
              </React.Fragment>)}
            {nothingVisible &&
              <div className="dropdown-item-text text-muted small">No matches</div>}
          </div>
          {props.extraOption &&
            <>
              <div className="dropdown-divider"></div>
              <FilterOption
                option={props.extraOption}
                checked={values.includes(props.extraOption.value)}
                onToggle={() => toggle(props.extraOption!.value)} />
            </>}
          {listed.length > 1 &&
            <>
              <div className="dropdown-divider"></div>
              <div className="d-flex gap-2 px-3 pb-2 pt-1 flex-shrink-0">
                <button
                  type="button"
                  className={footerButtonClass(allSelected)}
                  style={{ flex: '1 1 0' }}
                  disabled={allSelected}
                  onClick={() => change(selectable.map(option => option.value))}>
                  Select all
                </button>
                <button
                  type="button"
                  className={footerButtonClass(nothingSelected)}
                  style={{ flex: '1 1 0' }}
                  disabled={nothingSelected}
                  onClick={() => change([])}>
                  Clear
                </button>
              </div>
            </>}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}
